import { Stack } from "@mui/material";
import { motion } from "framer-motion";
import { HeatMixDetailItem } from "./HeatMixDetailItem";
import { MissingMixPlaceholder } from "./MissingMixPlaceholder";
import { HeatBasketComparisonView } from "./HeatBasketComparisonView";
import { MixedLiftsSummary } from "./MixedLiftsSummary";
import {
  HeatMix,
  MaterialRead,
  MixedMaterialPair,
} from "src/store/api/generatedApi";
import { useUnitsFormatter } from "hooks/formatters";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useMixQuantity } from "hooks/useMixQuantity";
import { useTenantData } from "contexts/tenant";
import { useMemo, useState } from "react";
import { MixDetailView, MixDisplayMode } from "../types";
import { ToggleSelector } from "../../ToggleSelector";
import { sorted } from "helpers";

export const HeatMixDetailsTab = ({
  mixes,
  mixedMaterialPairs,
  availableMixSources,
  ignoredMaterials,
  hasLifts,
  hasBaskets,
  materials,
  highlightedMaterial,
}: {
  mixes: HeatMix[];
  mixedMaterialPairs?: MixedMaterialPair[];
  availableMixSources: string[];
  ignoredMaterials: Set<string>;
  hasLifts: boolean;
  hasBaskets: boolean;
  materials: MaterialRead[];
  highlightedMaterial?: string;
}) => {
  const perBasketEnabled = useIsBehindFeatureFlag("per_basket_comparison");
  const mixQuantity = useMixQuantity();
  const { default_mix_breakdown } = useTenantData();
  const units = useUnitsFormatter(false);

  // The basket plot fills from the bottom, so we reverse material order
  const reversedMaterials = useMemo(
    () => sorted(materials, (a) => a.ordering, true),
    [materials]
  );
  const [displayMode, setDisplayMode] = useState<MixDisplayMode>(() => {
    if (hasLifts) return mixQuantity === "grabs" ? "liftRecipe" : "lifts";
    switch (default_mix_breakdown) {
      case "percentage":
        return "mass_fraction";
      case "small_mass":
        return "small_mass";
      case "mass":
      default:
        return "mass";
    }
  });
  const [mixDetailView, setMixDetailView] = useState<MixDetailView>("total");
  const displayModeLabels = {
    ...(hasLifts
      ? mixQuantity === "grabs"
        ? { liftRecipe: "liftRecipe", lifts: "totalLifts" }
        : { lifts: "totalLifts" }
      : {}),
    mass_fraction: units("mass_fraction", false),
    mass: units("mass", false),
    small_mass: units("small_mass", false),
  } as Record<MixDisplayMode, string>;
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Stack direction="row" gap={3} alignItems="center" sx={{ mt: 1, px: 1 }}>
        <ToggleSelector<MixDisplayMode>
          title="viewAs"
          selectedValue={displayMode}
          setSelectedValue={setDisplayMode}
          availableValueLabels={displayModeLabels}
        />
        {hasBaskets && perBasketEnabled ? (
          <ToggleSelector<MixDetailView>
            selectedValue={mixDetailView}
            setSelectedValue={setMixDetailView}
            availableValueLabels={{ total: "totalMix", perBasket: "perBasket" }}
          />
        ) : null}
      </Stack>
      {mixDetailView === "total" ? (
        <Stack direction="row" gap={2} sx={{ mt: 1, px: 1 }}>
          {availableMixSources.map((source, index) => {
            const stageMix = mixes.find((mix) => mix.source === source);
            const referenceMix =
              index === 0
                ? undefined
                : mixes.find(
                    (mix) => mix.source === availableMixSources[index - 1]
                  );
            return (
              <motion.div
                key={source}
                variants={itemVariants}
                whileHover={stageMix ? { scale: 1.02 } : undefined}
                transition={{ duration: 0.2 }}
              >
                {stageMix ? (
                  <HeatMixDetailItem
                    stageMix={stageMix}
                    referenceMix={referenceMix}
                    isFirst={index === 0}
                    displayMode={displayMode}
                    ignoredMaterials={ignoredMaterials}
                    materialMetadata={reversedMaterials}
                    highlightedMaterial={highlightedMaterial}
                  />
                ) : (
                  <MissingMixPlaceholder source={source} />
                )}
              </motion.div>
            );
          })}
        </Stack>
      ) : (
        <HeatBasketComparisonView
          mixes={mixes}
          availableMixSources={availableMixSources}
          ignoredMaterials={ignoredMaterials}
          materials={materials}
          displayMode={displayMode}
        />
      )}
      <MixedLiftsSummary
        pairs={mixedMaterialPairs ?? []}
        materials={materials}
      />
    </motion.div>
  );
};
